import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function getHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  logout: () => axios.post(`${API}/auth/logout`, {}, { headers: getHeaders() }),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getHeaders() }),

  // Reports
  getReports: (status, roleFilter, centre, fromDate, toDate) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (roleFilter) params.append('role_filter', roleFilter);
    if (centre) params.append('centre', centre);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API}/reports${qs}`, { headers: getHeaders() });
  },
  getReport: (id) => axios.get(`${API}/reports/${id}`, { headers: getHeaders() }),
  createReport: (data) => axios.post(`${API}/reports`, data, { headers: getHeaders() }),
  updateReport: (id, data) => axios.put(`${API}/reports/${id}`, data, { headers: getHeaders() }),
  deleteReport: (id) => axios.delete(`${API}/reports/${id}`, { headers: getHeaders() }),
  submitReport: (id) => axios.post(`${API}/reports/${id}/submit`, {}, { headers: getHeaders() }),
  reviewReport: (id, data) => axios.post(`${API}/reports/${id}/review`, data, { headers: getHeaders() }),

  // Dashboard
  getDashboardStats: (centre, fromDate, toDate) => {
    const params = new URLSearchParams();
    if (centre && centre !== 'All') params.append('centre', centre);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API}/dashboard/stats${qs}`, { headers: getHeaders() });
  },
  getFacultyComparison: (centre) => axios.get(`${API}/dashboard/faculty-comparison${centre && centre !== 'All' ? `?centre=${encodeURIComponent(centre)}` : ''}`, { headers: getHeaders() }),

  // Responsibility Groups
  getMyResponsibilityGroups: () => axios.get(`${API}/responsibility-groups-me`, { headers: getHeaders() }),
  getResponsibilityGroups: (facultyId) => axios.get(`${API}/responsibility-groups/${facultyId}`, { headers: getHeaders() }),
  deleteResponsibilityGroup: (facultyId, groupName) => axios.delete(`${API}/responsibility-groups/${facultyId}/${encodeURIComponent(groupName)}`, { headers: getHeaders() }),
  renameResponsibilityGroup: (facultyId, oldName, newName) => axios.put(`${API}/responsibility-groups/${facultyId}/rename`, { old_name: oldName, new_name: newName }, { headers: getHeaders() }),
  getCICComparison: (centre) => axios.get(`${API}/dashboard/cic-comparison${centre && centre !== 'All' ? `?centre=${encodeURIComponent(centre)}` : ''}`, { headers: getHeaders() }),

  // Export
  exportReportPDF: (id) => axios.get(`${API}/reports/${id}/export/pdf`, { headers: getHeaders(), responseType: 'blob' }),
  exportReportExcel: (id) => axios.get(`${API}/reports/${id}/export/excel`, { headers: getHeaders(), responseType: 'blob' }),
  exportReportsBulkExcel: (status) => {
    const params = status ? `?status=${status}` : '';
    return axios.get(`${API}/reports/export/excel${params}`, { headers: getHeaders(), responseType: 'blob' });
  },

  // Messages
  getMessages: (reportId) => axios.get(`${API}/reports/${reportId}/messages`, { headers: getHeaders() }),
  sendMessage: (reportId, message, visibleToFaculty, activitySection, activityItemId) => axios.post(`${API}/reports/${reportId}/messages`, { message, visible_to_faculty: visibleToFaculty, activity_section: activitySection, activity_item_id: activityItemId }, { headers: getHeaders() }),
  markMessagesRead: (reportId) => axios.post(`${API}/reports/${reportId}/messages/read`, {}, { headers: getHeaders() }),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`, { headers: getHeaders() }),

  // Close out overdue
  closeItem: (reportId, section, itemId, closingDate) => axios.post(`${API}/reports/${reportId}/close-item`, { section, item_id: itemId, closing_date: closingDate }, { headers: getHeaders() }),

  // User Management
  getUsers: () => axios.get(`${API}/users`, { headers: getHeaders() }),
  syncUsers: () => axios.post(`${API}/users/sync`, {}, { headers: getHeaders() }),
  getSyncStatus: () => axios.get(`${API}/users/sync-status`, { headers: getHeaders() }),
  updateUserRole: (userId, role, designation) => axios.put(`${API}/users/${userId}/role`, { role, designation }, { headers: getHeaders() }),
  updateUserIncharges: (userId, inchargeIds) => axios.put(`${API}/users/${userId}/incharges`, { incharge_ids: inchargeIds }, { headers: getHeaders() }),
  updateUserInline: (userId, data) => axios.put(`${API}/users/${userId}/inline`, data, { headers: getHeaders() }),
  getCentres: () => axios.get(`${API}/centres`, { headers: getHeaders() }),
  addCentre: (name) => axios.post(`${API}/centres`, { name }, { headers: getHeaders() }),
  deleteCentre: (name) => axios.delete(`${API}/centres/${encodeURIComponent(name)}`, { headers: getHeaders() }),

  // Courses (Super Admin)
  getCourses: () => axios.get(`${API}/courses`, { headers: getHeaders() }),
  createCourse: (name, code) => axios.post(`${API}/courses`, { name, code }, { headers: getHeaders() }),
  updateCourse: (id, name, code) => axios.put(`${API}/courses/${id}`, { name, code }, { headers: getHeaders() }),
  deleteCourse: (id) => axios.delete(`${API}/courses/${id}`, { headers: getHeaders() }),

  // Pending notifications
  getPendingNotifications: () => axios.get(`${API}/notifications/pending`, { headers: getHeaders() }),

  // Blocker settings
  getBlockerDays: () => axios.get(`${API}/settings/blocker-days`, { headers: getHeaders() }),
  setBlockerDays: (role, days) => axios.put(`${API}/settings/blocker-days`, { role, days }, { headers: getHeaders() }),

  // Designations
  getDesignations: () => axios.get(`${API}/designations`, { headers: getHeaders() }),
  createDesignation: (name) => axios.post(`${API}/designations`, { name }, { headers: getHeaders() }),
  deleteDesignation: (name) => axios.delete(`${API}/designations/${encodeURIComponent(name)}`, { headers: getHeaders() }),

  // Tasks
  getTasks: (assignedTo, status) => {
    const params = new URLSearchParams();
    if (assignedTo) params.append('assigned_to', assignedTo);
    if (status) params.append('status', status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API}/tasks${qs}`, { headers: getHeaders() });
  },
  createTask: (data) => axios.post(`${API}/tasks`, data, { headers: getHeaders() }),
  getMyTasks: () => axios.get(`${API}/tasks/my`, { headers: getHeaders() }),
  getMyTaskCounts: () => axios.get(`${API}/tasks/my-counts`, { headers: getHeaders() }),
  completeTask: (taskId, completedDate) => axios.post(`${API}/tasks/${taskId}/complete`, { completed_date: completedDate }, { headers: getHeaders() }),
  addTaskComment: (taskId, comment) => axios.post(`${API}/tasks/${taskId}/comment`, { comment }, { headers: getHeaders() }),
  getTaskAnalytics: ({ days, fromDate, toDate, centre, search } = {}) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (!fromDate && !toDate && days) params.append('period_days', days);
    if (centre && centre !== 'All') params.append('centre', centre);
    if (search && search.trim()) params.append('search', search.trim());
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API}/tasks/analytics${qs}`, { headers: getHeaders() });
  },
  sendTaskReminder: (taskId) => axios.post(`${API}/tasks/${taskId}/remind`, {}, { headers: getHeaders() }),
  sendBulkTaskReminders: (taskIds) => axios.post(`${API}/tasks/remind-bulk`, { task_ids: taskIds }, { headers: getHeaders() }),
  resetUserPassword: (userId) => axios.post(`${API}/users/${userId}/reset-password`, {}, { headers: getHeaders() }),
  getTaskNotifications: () => axios.get(`${API}/task-notifications`, { headers: getHeaders() }),
  markTaskNotificationsRead: () => axios.post(`${API}/task-notifications/read`, {}, { headers: getHeaders() }),

  // Timetable
  ttSubjects: (centre) => axios.get(`${API}/timetable/subjects`, { headers: getHeaders(), params: centre ? { centre } : {} }),
  ttCreateSubject: (d) => axios.post(`${API}/timetable/subjects`, d, { headers: getHeaders() }),
  ttUpdateSubject: (id, d) => axios.put(`${API}/timetable/subjects/${id}`, d, { headers: getHeaders() }),
  ttDeleteSubject: (id) => axios.delete(`${API}/timetable/subjects/${id}`, { headers: getHeaders() }),
  ttBatches: (centre) => axios.get(`${API}/timetable/batches`, { headers: getHeaders(), params: centre ? { centre } : {} }),
  ttCreateBatch: (d) => axios.post(`${API}/timetable/batches`, d, { headers: getHeaders() }),
  ttUpdateBatch: (id, d) => axios.put(`${API}/timetable/batches/${id}`, d, { headers: getHeaders() }),
  ttDeleteBatch: (id) => axios.delete(`${API}/timetable/batches/${id}`, { headers: getHeaders() }),
  ttPeriods: (centre) => axios.get(`${API}/timetable/periods`, { headers: getHeaders(), params: centre ? { centre } : {} }),
  ttCreatePeriod: (d) => axios.post(`${API}/timetable/periods`, d, { headers: getHeaders() }),
  ttUpdatePeriod: (id, d) => axios.put(`${API}/timetable/periods/${id}`, d, { headers: getHeaders() }),
  ttDeletePeriod: (id) => axios.delete(`${API}/timetable/periods/${id}`, { headers: getHeaders() }),
  ttFacultyOptions: (centre) => axios.get(`${API}/timetable/faculty-options`, { headers: getHeaders(), params: centre ? { centre } : {} }),
  ttTimetables: (centre) => axios.get(`${API}/timetable/timetables`, { headers: getHeaders(), params: centre ? { centre } : {} }),
  ttGetTimetable: (id) => axios.get(`${API}/timetable/timetables/${id}`, { headers: getHeaders() }),
  ttCreateTimetable: (d) => axios.post(`${API}/timetable/timetables`, d, { headers: getHeaders() }),
  ttUpdateTimetable: (id, d) => axios.put(`${API}/timetable/timetables/${id}`, d, { headers: getHeaders() }),
  ttDeleteTimetable: (id) => axios.delete(`${API}/timetable/timetables/${id}`, { headers: getHeaders() }),
  ttPublishTimetable: (id) => axios.post(`${API}/timetable/timetables/${id}/publish`, {}, { headers: getHeaders() }),
  ttMyTimetable: () => axios.get(`${API}/timetable/my`, { headers: getHeaders() }),
  ttExportTimetable: (id, params) => axios.get(`${API}/timetable/timetables/${id}/export`, { headers: getHeaders(), params, responseType: 'blob' }),
  ttCsvTemplate: (id) => axios.get(`${API}/timetable/timetables/${id}/csv-template`, { headers: getHeaders(), responseType: 'blob' }),
  ttCsvUpload: (id, formData) => axios.post(`${API}/timetable/timetables/${id}/csv-upload`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }),
  ttCheckConflicts: (id, entries) => axios.post(`${API}/timetable/timetables/${id}/check-conflicts`, { entries }, { headers: getHeaders() }),
  ttHoursDashboard: (centre) => axios.get(`${API}/timetable/hours-dashboard`, { headers: getHeaders(), params: centre ? { centre } : {} }),
  ttHoursDashboardExport: (centre, format) => axios.get(`${API}/timetable/hours-dashboard/export`, { headers: getHeaders(), params: { centre, format }, responseType: 'blob' }),

  // Faculty Allocation Report
  generateAllocationReport: (weekLabel, timetableIds) => axios.post(`${API}/timetable/allocation-reports/generate`, { week_label: weekLabel, timetable_ids: timetableIds }, { headers: getHeaders() }),
  getAllocationReports: () => axios.get(`${API}/timetable/allocation-reports`, { headers: getHeaders() }),
  getAllocationReport: (id) => axios.get(`${API}/timetable/allocation-reports/${id}`, { headers: getHeaders() }),
  forwardAllocationReport: (id) => axios.post(`${API}/timetable/allocation-reports/${id}/forward`, {}, { headers: getHeaders() }),
  deleteAllocationReport: (id) => axios.delete(`${API}/timetable/allocation-reports/${id}`, { headers: getHeaders() }),
  exportAllocationReport: (id, params) => axios.get(`${API}/timetable/allocation-reports/${id}/export`, { headers: getHeaders(), params, responseType: 'blob' }),

  // Leave / Absent
  markLeave: (date, reason) => axios.post(`${API}/leave/mark`, { date, reason }, { headers: getHeaders() }),
  getMyLeaves: () => axios.get(`${API}/leave/my`, { headers: getHeaders() }),
  cancelLeave: (date) => axios.delete(`${API}/leave/${date}`, { headers: getHeaders() }),

  // Weekly Summary
  generateWeeklySummary: (weekFrom, weekTo, summaryType) => axios.post(`${API}/weekly-summary/generate`, { week_from: weekFrom, week_to: weekTo, summary_type: summaryType || 'faculty' }, { headers: getHeaders() }),
  getWeeklySummaries: (summaryType) => axios.get(`${API}/weekly-summary${summaryType ? `?summary_type=${summaryType}` : ''}`, { headers: getHeaders() }),
  getWeeklySummaryAlerts: () => axios.get(`${API}/weekly-summary/alerts`, { headers: getHeaders() }),
  getWeeklySummary: (id, criticalOnly) => axios.get(`${API}/weekly-summary/${id}${criticalOnly ? '?critical_only=true' : ''}`, { headers: getHeaders() }),
  updateWeeklySummary: (id, data) => axios.put(`${API}/weekly-summary/${id}`, data, { headers: getHeaders() }),
  deleteWeeklySummary: (id) => axios.delete(`${API}/weekly-summary/${id}`, { headers: getHeaders() }),
  weeklySummaryAction: (id, action, comments, targetRole) => axios.post(`${API}/weekly-summary/${id}/action`, { action, comments, target_role: targetRole }, { headers: getHeaders() }),

  // Centre Staff Status (Task 2) + Clarification SLA (Task 3)
  getCentreStaffStatus: (centre, fromDate, toDate) => {
    const params = new URLSearchParams();
    if (centre) params.append('centre', centre);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API}/dashboard/centre-staff-status${qs}`, { headers: getHeaders() });
  },
  getOverdueClarifications: () => axios.get(`${API}/clarifications/overdue`, { headers: getHeaders() }),
  getCEOInsights: (fromDate, toDate) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.get(`${API}/dashboard/ceo-insights${qs}`, { headers: getHeaders() });
  },
};

export default api;
